/* eslint-disable jsx-a11y/no-redundant-roles */
/* eslint-disable jsx-a11y/role-supports-aria-props */
import { DownIcon, UpIcon } from '@/components/icons';
import GlobalIcon from '@/components/icons/GlobalIcon';
import clsx from 'clsx';
import { forwardRef, useImperativeHandle } from 'react';
import styles from './styles.module.scss';
import { useLanguageDropdown } from './useLanguageDropdown';

export type LanguageDropdownHandle = {
    close: () => void;
};

export type LanguageDropdownProps = {
    onBarrierFreeUsed?: () => void;
    onZoomButtonsOpen?: (isOpen: boolean) => void;
    regionNav?: boolean;
    regionOrder?: number;
    className?: string;
    variant?: 'dropdown' | 'pills';
};

export const LanguageDropdown = forwardRef<LanguageDropdownHandle, LanguageDropdownProps>(
    ({ onBarrierFreeUsed, onZoomButtonsOpen, regionNav, regionOrder, className, variant = 'dropdown' }, ref) => {
        const {
            ui,
            isOpen,
            dropdownRef,
            buttonRef,
            languageButtonLabel,
            resolveLanguageOptionLabel,
            handleLanguageSelect,
            handleToggleDropdown,
            handleKeyDown,
            speakOnFocus,
            handleTriggerFocus,
            closeDropdown,
            t,
        } = useLanguageDropdown({ onBarrierFreeUsed, onZoomButtonsOpen });

        useImperativeHandle(ref, () => ({
            close: closeDropdown,
        }));

        if (variant === 'pills') {
            return (
                <div
                    className={clsx(styles['language-pills'], className)}
                    data-tts-scope="dropdown"
                    ref={dropdownRef}
                    data-region-nav={regionNav ? true : undefined}
                    data-region-order={regionOrder}
                >
                    <div className={styles['language-pills__list']} role="tablist" aria-label={t('footer.barrier_free.language')}>
                        {ui.languageOptions.map((option) => {
                            const optionLabel = resolveLanguageOptionLabel(option);
                            const isSelected = option.value === ui.language;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="tab"
                                    data-tts-scope="dropdown"
                                    aria-label={optionLabel}
                                    aria-checked={isSelected}
                                    aria-selected={isSelected}
                                    className={clsx(styles['language-pills__item'], {
                                        [styles['language-pills__item--selected']]: isSelected,
                                    })}
                                    onClick={() => handleLanguageSelect(option.value, optionLabel)}
                                    onFocus={(e) => {
                                        speakOnFocus(e.target);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleLanguageSelect(option.value, optionLabel);
                                        }
                                    }}
                                    data-interactive
                                    tabIndex={0}
                                >
                                    <span className={option.value === 'en' ? 'font-number' : ''}>{optionLabel}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        return (
            <div
                className={clsx(styles['language-dropdown'], className)}
                data-tts-scope="dropdown"
                ref={dropdownRef}
                data-region-nav={regionNav ? true : undefined}
                data-region-order={regionOrder}
            >
                <button
                    ref={buttonRef}
                    type="button"
                    data-role={t('common.data_role.button')}
                    className={styles['language-dropdown__trigger']}
                    aria-label={t('footer.barrier_free.language')}
                    aria-expanded={isOpen}
                    data-interactive
                    onClick={handleToggleDropdown}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => {
                        handleTriggerFocus(e.target);
                    }}
                >
                    <GlobalIcon />
                    <span className={clsx('font-number', styles['language-dropdown__trigger-label'])}>{languageButtonLabel}</span>
                    {isOpen ? <UpIcon /> : <DownIcon />}
                </button>

                {isOpen && (
                    <div className={styles['language-dropdown__options']} role="tablist" aria-label={t('footer.barrier_free.language')}>
                        {ui.languageOptions.map((option) => {
                            const optionLabel = resolveLanguageOptionLabel(option);
                            const isSelected = option.value === ui.language;

                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="button"
                                    data-tts-scope="dropdown"
                                    aria-label={optionLabel}
                                    aria-checked={isSelected}
                                    aria-selected={isSelected}
                                    className={clsx(styles['language-dropdown__options__item'], {
                                        [styles['language-dropdown__options__item--selected']]: isSelected,
                                    })}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleLanguageSelect(option.value, optionLabel);
                                    }}
                                    onFocus={(e) => {
                                        speakOnFocus(e.target);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleLanguageSelect(option.value, optionLabel);
                                        }
                                    }}
                                    data-interactive
                                    tabIndex={0}
                                    disabled={!isOpen}
                                >
                                    <span className={option.value === 'en' ? 'font-number' : ''}>{optionLabel}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
);

LanguageDropdown.displayName = 'LanguageDropdown';

export { useLanguageDropdown } from './useLanguageDropdown';
